import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatDateTimeWAT, relativeTime } from "@/lib/format";
import { Mail, Phone, Inbox } from "lucide-react";

export const InboxAdmin = () => {
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/contact");
    setMessages(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (m) => {
    setSelected(m);
    if (!m.read) {
      await api.put(`/contact/${m.id}/read`);
      load();
    }
  };

  const unread = messages.filter(m => !m.read).length;

  return (
    <div className="grid lg:grid-cols-3 gap-4" style={{ minHeight: "calc(100vh - 220px)" }}>
      <div className="s-card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-dark)" }}>
          <div className="flex items-center gap-2">
            <Inbox size={16} color="#2ECAC8" />
            <div className="h-section" style={{ fontSize: 15 }}>Inbox</div>
          </div>
          <span className="badge badge-neutral">{unread} unread</span>
        </div>
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {messages.length === 0 && (
            <div className="text-muted text-center p-10" style={{ fontSize: 13 }}>
              No messages yet. New website enquiries land here.
            </div>
          )}
          {messages.map(m => (
            <button key={m.id} onClick={() => open(m)}
                    className="w-full text-left p-4"
                    style={{
                      borderBottom: "1px solid var(--border-dark)",
                      background: selected?.id === m.id
                        ? "rgba(46,202,200,0.08)"
                        : m.read ? "transparent" : "rgba(46,202,200,0.03)",
                    }}
                    data-testid={`inbox-${m.id}`}>
              <div className="flex justify-between items-start mb-1">
                <div style={{ fontSize: 14, color: "#fff" }}>{m.name}</div>
                {!m.read && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#2ECAC8" }} />}
              </div>
              <div className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>{m.subject}</div>
              <div className="text-muted mono" style={{ fontSize: 11 }}>{relativeTime(m.created_at)}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 s-card">
        {selected ? (
          <>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="h-section" style={{ fontSize: 18 }}>{selected.subject}</div>
                <div className="text-muted mono" style={{ fontSize: 12, marginTop: 4 }}>
                  {formatDateTimeWAT(selected.created_at)}
                </div>
              </div>
              <span className="badge badge-neutral">→ {selected.delivered_to || "admin@sirodsecur.com"}</span>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mb-5">
              <div className="p-3 rounded-lg" style={{ background: "rgba(46,202,200,0.05)", border: "1px solid var(--border-dark)" }}>
                <div className="text-muted" style={{ fontSize: 11 }}>FROM</div>
                <div style={{ fontSize: 14, color: "#fff", marginTop: 4 }}>{selected.name}</div>
                <a href={`mailto:${selected.email}`} className="text-teal inline-flex items-center gap-1 mt-2 mono" style={{ fontSize: 12 }}>
                  <Mail size={12} /> {selected.email}
                </a>
              </div>
              {selected.phone && (
                <div className="p-3 rounded-lg" style={{ background: "rgba(46,202,200,0.05)", border: "1px solid var(--border-dark)" }}>
                  <div className="text-muted" style={{ fontSize: 11 }}>PHONE</div>
                  <a href={`tel:${selected.phone}`} className="text-white inline-flex items-center gap-1 mt-2 mono" style={{ fontSize: 14 }}>
                    <Phone size={12} /> {selected.phone}
                  </a>
                </div>
              )}
            </div>

            <div className="text-muted" style={{ fontSize: 11, letterSpacing: "0.06em", marginBottom: 8 }}>MESSAGE</div>
            <div style={{ fontSize: 14, lineHeight: 1.7, color: "#fff", whiteSpace: "pre-wrap" }}>
              {selected.message}
            </div>

            <div className="flex gap-2 mt-6">
              <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                 className="btn-primary inline-flex items-center gap-2" data-testid="reply-btn">
                <Mail size={14} /> Reply via email
              </a>
            </div>
          </>
        ) : (
          <div className="text-muted text-center" style={{ padding: 40, fontSize: 13 }}>
            <Inbox size={20} className="mx-auto mb-2" />
            Select a message to read.
          </div>
        )}
      </div>
    </div>
  );
};
